/**
 * „Frühere Verträge" im Zählerdetail (Spec 46c): jeder Vertrag ohne
 * `is_current`, in Serverreihenfolge. Anbieter, `period_display` und
 * `status.label` kommen wörtlich vom Server.
 */

import type { SupplyContract } from '@/features/energy/api'

interface SupplyHistoryProps {
  /** Alle Verträge ohne `is_current`, in Serverreihenfolge. */
  readonly contracts: readonly SupplyContract[]
  readonly canEdit: boolean
  readonly onEdit: (contract: SupplyContract) => void
}

/** „Frühere Verträge": nur mit mindestens einem Eintrag; ein künftiger Vertrag
 * neben einem laufenden steht hier als „Künftig". */
export function SupplyHistory({ contracts, canEdit, onEdit }: SupplyHistoryProps) {
  if (contracts.length === 0) return null

  return (
    <section data-testid="meter-supply-history" className="flex flex-col gap-2">
      <h3 className="text-label text-muted font-medium">Frühere Verträge</h3>
      <ul className="border-border bg-surface rounded-card border">
        {contracts.map((contract) => {
          const content = (
            <>
              <span className="flex min-w-0 flex-1 flex-col text-left">
                <span className="text-body text-text truncate font-semibold">{contract.provider?.label ?? contract.provider_label}</span>
                <span className="text-label text-muted tabular-nums">
                  {contract.period_display}
                </span>
              </span>
              <span className="text-label text-muted shrink-0">{contract.status.label}</span>
            </>
          )
          const rowClass = 'border-border flex min-h-(--row-h) items-center gap-3 border-b px-4 py-2 last:border-b-0'

          return (
            <li key={contract.id} data-testid={`meter-supply-row-${contract.id}`}>
              {canEdit ? (
                <button
                  type="button"
                  data-testid={`meter-supply-open-${contract.id}`}
                  onClick={() => {
                    onEdit(contract)
                  }}
                  className={`${rowClass} hover:bg-bg w-full transition-colors duration-(--duration-fast) ease-(--ease-out)`}
                >
                  {content}
                </button>
              ) : (
                <div className={rowClass}>{content}</div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
